import styled from "styled-components";
import { IconButton, IContextualMenuProps, IContextualMenuItem } from "@fluentui/react";
import React, { useContext, useMemo } from "react";
import { SettingButtonsDisable } from "./types";
import { ColumnOptionsContext } from "./ColumnOptions";
import { ViewDialogState } from "./ColumnOptionsPanel";
import { isColumnFiltered } from "./utils";

type Props = {
  setViewDialog: (viewDialogState: ViewDialogState) => void;
  disableButtons?: SettingButtonsDisable;
};

export const ViewContextButton = ({ setViewDialog, disableButtons }: Props) => {
  const {
    selectedView,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    columns,
    strings,
    hideViewOptionsFeatures,
    disableNewView,
  } = useContext(ColumnOptionsContext);

  const areAnyFiltersSelected = useMemo(() => {
    return columns.some((c) => isColumnFiltered(c.filter));
  }, [columns]);

  const menuItems: IContextualMenuItem[] = [];

  if (!hideViewOptionsFeatures) {
    menuItems.push({
      key: "new",
      text: strings.newViewButtonLabel,
      iconProps: { iconName: "Add" },
      disabled: disableButtons?.new,
      onClick: () => {
        setViewDialog({ isOpen: true, viewId: null });
      },
    });
    menuItems.push({
      key: "save",
      text: strings.saveViewButtonLabel,
      iconProps: { iconName: "Save" },
      disabled: disableButtons?.save || !selectedView,
      onClick: () => {
        onClickSaveView();
      },
    });
    menuItems.push({
      key: "edit",
      text: strings.editViewButtonLabel,
      iconProps: { iconName: "Edit" },
      disabled: disableButtons?.edit || !selectedView,
      onClick: () => {
        if (!selectedView) return;
        setViewDialog({ isOpen: true, viewId: selectedView.id });
      },
    });
  }

  menuItems.push({
    key: "reset",
    text: strings.resetButtonLabel,
    iconProps: { iconName: "Undo" },
    disabled: disableButtons?.reset,
    onClick: () => {
      onClickReset();
    },
  });

  menuItems.push({
    key: "clearFilters",
    text: strings.clearFiltersButtonLabel,
    iconProps: { iconName: "ClearFilter" },
    disabled: disableButtons?.clearAllFilters || !areAnyFiltersSelected,
    onClick: () => {
      onClickClearAllFilters();
    },
  });

  if (!hideViewOptionsFeatures) {
    menuItems.push({
      key: "delete",
      text: strings.deleteViewButtonLabel,
      iconProps: { iconName: "Delete" },
      disabled: disableButtons?.delete || !selectedView,
      onClick: () => {
        onClickDeleteView();
      },
    });
  }

  const menuProps: IContextualMenuProps = {
    items: menuItems,
  };

  return (
    <StyledIconButton
      iconProps={{ iconName: "MoreVertical" }}
      menuProps={menuProps}
      disabled={typeof disableNewView === "undefined" ? false : disableNewView}
    />
  );
};

const StyledIconButton = styled(IconButton)`
  &:hover {
    background-color: ${(props) => props.theme.palette.neutralLighter};
  }
`;
